import React from 'react';
import { FormInstance, notification } from 'antd';
import { AxiosError } from 'axios';
import { invertBy } from 'lodash';

import { StyledColumn } from '../components/StyledColumn';
import { CandidateRecruitmentType } from '../features/candidates/Candidates.types';

import { SearchFormFieldsData } from './searchFormField.types';

const fieldErrorsTitles = {
  missing: 'Following fields are required:',
  'not-future': 'Date must be in the future:',
  'already-in-project': 'Candidate already in project:',
  'already-has-candidate': 'Project already has this candidate:',
  duplicate: 'Following fields cannot be duplicates:',
  'invalid-value': 'Following fields have invalid value:',
  'too-big': 'Following fields are too big:',
  'has-references': 'This entry has references to other elements and cannot be removed.',
  others: 'Following errrors occured:',
};

export const displayErrorNotification = (title: string, field?: string) =>
  notification.error({
    message: 'Error',
    description: (
      <StyledColumn gap="0.5rem">
        <div>{title}</div>
        {field && <div>- {field}</div>}
      </StyledColumn>
    ),
  });

const ErrorsList: React.FC<{ fields: string[]; title: string; errorsMapper?: Record<string, string> }> = ({
  fields,
  title,
  errorsMapper,
}) => {
  return (
    <StyledColumn gap="0.3rem">
      <div>{title}</div>
      <StyledColumn gap="0">
        {fields.map((field, index) => {
          const errorFieldLabel =
            errorsMapper?.[field] ?? SearchFormFieldsData[field as keyof typeof SearchFormFieldsData]?.label;
          return <div key={index}>- {errorFieldLabel ?? field}</div>;
        })}
      </StyledColumn>
    </StyledColumn>
  );
};

export const clearFieldErrors = (form: FormInstance) => {
  const fields = form.getFieldsValue();
  const errorFields = Object.keys(fields)
    .filter((name) => form.getFieldError(name).length)
    .map((name) => ({ name, errors: [] }));
  form.setFields(errorFields);
};

const parseErrorMessage = (error: string) => {
  const errorElements = error.split('.');
  return {
    elementId: errorElements[1]?.substring(1, errorElements[1].length - 1),
    fieldName: errorElements[2]?.substring(1, errorElements[2].length - 1),
  };
};

const getErrorMessages = (error: AxiosError<unknown, unknown>) => {
  const data = error.response?.data;
  if (!data || typeof data !== 'object' || !('messages' in data)) return undefined;

  const { messages } = data;
  if (!messages || typeof messages !== 'object') return undefined;

  return Object.entries(messages).every(([key, value]) => typeof key === 'string' && typeof value === 'string')
    ? (messages as Record<string, string>)
    : undefined;
};

export const handleBulkRecruitmentsUpdateErrors = (
  error: AxiosError<unknown, unknown>,
  form: FormInstance,
  recruitments: CandidateRecruitmentType[]
) => {
  const errorMessages = getErrorMessages(error);
  if (!errorMessages) return;

  const responsibleErrors = Object.keys(errorMessages).flatMap((elm) => {
    const { elementId, fieldName } = parseErrorMessage(elm);
    const recruitmentName = recruitments.find((recruitment) => recruitment.uuid === elementId)?.project.name ?? '';
    return fieldName === 'responsible' ? [{ type: errorMessages[elm], fieldName, recruitmentName }] : [];
  });

  const title =
    responsibleErrors[0]?.type === 'missing' ? 'Responsible field is required' : 'Invalid Responsible for recruitment:';
  const recruitmentName = responsibleErrors[0]?.recruitmentName;
  displayErrorNotification(title, recruitmentName);

  clearFieldErrors(form);
  form.setFields([{ name: 'responsible', errors: [''] }]);
};

const handleErrorNotification = (
  errorsByType: Record<string, string[]>,
  mapErrorFieldsToFormLabels?: Record<string, string>
) => {
  notification.error({
    message: 'Error',
    description: (
      <StyledColumn gap="0.5rem">
        {Object.keys(errorsByType).map(
          (type) =>
            errorsByType?.[type]?.length && (
              <ErrorsList
                fields={errorsByType[type] ?? []}
                title={fieldErrorsTitles[type as keyof typeof fieldErrorsTitles]}
                errorsMapper={mapErrorFieldsToFormLabels}
                key={type}
              />
            )
        )}
      </StyledColumn>
    ),
  });
};

const handleFormError = (errorsByType: Record<string, string[]>, form?: FormInstance) => {
  if (!form) return;
  clearFieldErrors(form);
  Object.keys(errorsByType).forEach((type) =>
    form.setFields((errorsByType?.[type] ?? []).map((field) => ({ name: field, errors: [''] })))
  );
};

export const handleFormErrors = ({
  error,
  form,
  mapErrorFieldsToFormLabels,
}: {
  error: AxiosError<unknown, unknown>;
  form?: FormInstance;
  mapErrorFieldsToFormLabels?: Record<string, string>;
}) => {
  const errorMessages = getErrorMessages(error);
  if (!errorMessages) return;
  const errorsByType = invertBy(errorMessages) ?? {};
  handleErrorNotification(errorsByType, mapErrorFieldsToFormLabels);
  handleFormError(errorsByType, form);
};
